/* eslint-disable max-lines, react/jsx-props-no-spreading */
import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import posthog from 'posthog-js'
import cookie from 'js-cookie'
import Moment from 'moment'
import { Routes, useLocation } from 'react-router-dom'
import styled from '@emotion/styled'
import { Global, css } from '@emotion/react'
import { datadogRum } from '@datadog/browser-rum'
import { DESKTOP_BREAKPOINT, useTheme, Card, useModal } from '@xfers/design-system'
import { useAppFlippers } from '~/store/flippers/hooks'
import {
  selectUserEmail,
  selectUserName,
  selectIsKillSwitchActive,
  selectIsDpt2TncAccepted,
  selectIsUserFullyVerified,
} from '~/app/store/userInfo/selectors'
import { FLIPPER_KEYS } from '~/store/flippers/const'
import {
  selectFlipperWithId,
  selectIsAssetUnificationPhase1Enabled,
  selectIsAssetUnificationPhase1Ready,
  selectIsOtcTrxHistoryEnabled,
} from '~/store/flippers/selectors'
import { setBlockchainAddressList } from '~/app/store/blockchainAddresses/actions'
import { CkaTransactionRestrictionDate } from '~/app/store/landingPage/const'

import {
  init,
  setIsLockedFeatureOn,
  setIsTypeformSurveyRequired,
  setIsTypeformSurveyVisible,
} from '~/app/store/route/actions'
import {
  setIsDptApplied,
  setIsHtsWithdrawalFeeCharging,
  setIsHederaUIEnabled,
  setIsPolygonUIEnabled,
  setIsSsoTwoFaAuthenticatorEnabled,
  setIsDptRiskModalEnabled,
  setIsFazzVaForDashboardEnabled,
  setIsFazzVaForDirectMintEnabled,
  setIsTravelRuleDepositDeclarationEnabled,
  setIsTravelRuleAddrWhitelistingEnabled,
  setIsAvalancheUIEnabled,
  setIsBnbsmartUIEnabled,
  setIsTronUIEnabled,
  setIsArbitrumUIEnabled,
  setIsRippleUIEnabled,
  setIsCkaEnabled,
  setIsUsdtBep20WithdrawalEnabled,
  setIsUsdtBep20DepositEnabled,
  setIsUsdtBep20UIEnabled,
  setIsXsgdSplUIEnabled,
  setIsXusdSplUIEnabled,
  setIsSolanaUIEnabled,
} from '~/app/store/landingPage/actions'
import { setIsEarnEnabled } from '~/app/store/earn/actions'
import RouteHooks from './RouteHooks'
import { straitsXRoutes } from '~/app/routes/config'
import { PrivateRoute } from '~/components/Route'
import StraitsXFooter from '~/components/StraitsXFooter'
import StraitsXNavigation from '~/components/StraitsXNavigation'
import StraitsXUserInfoBar from '~/components/StraitsXUserInfoBar'
import KillSwitchLockedPage from '~/components/KillSwitchLockedPage'
import { RootState } from '~/app/store/types'
import Tour from '~/app/pages/Tour'
import { Notification } from './store/route/types'
import FeatureGatingManager from '~/managers/FeatureGatingManager'
import {
  getLatestTransactions,
  getTotalTravelRuleComplianceBlockerCount,
} from './store/transactions/actions'
import { fetchUserInfo } from './store/userInfo/actions'
import {
  selectUserInfo,
  selectUserVerificationStatus,
  selectUserRequiresManualPeriodicReview,
} from './store/userInfo/selectors'
import {
  selectBlockchainAddressList,
  selectIsBlockchainAddressesInitiated,
} from './store/blockchainAddresses/selectors'
import { fetchBankList } from './store/bankAccounts/actions'
import {
  fetchBlockchainAddresses,
  setIsWhitelistingImprovementEnabled,
} from './store/blockchainAddresses/actions'
import { selectDgwAccountByCurrency } from './store/accounts/selectors'
import { CURRENCY } from '~/types'
import { fetchAccountDetailList, fetchAccountList } from './store/accounts/actions'
import { ConfigManager, DEPLOY_ENVIRONMENT, SITE_TYPE } from '~/managers/ConfigManager'
import Dpt2Tnc from '~/components/Dpt2Tnc'
import { PATHS } from './routes/paths'
import { ErrorBoundary } from '~/components/ErrorBoundary/ErrorBoundary'
import { useAutoAddPosthogIds } from '~/hooks/useAutoAddPosthogIds'
import SSOManager from '~/managers/SSOManager'
import { useShield } from '~/hooks/useShield'
import { fetchAssetList } from '~/store/assets/actions'
import { fetchLatestTransactions } from '~/store/transactions/actions'

export default () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { resetModal } = useModal()
  useAutoAddPosthogIds(SITE_TYPE.APP)
  const isSandbox = false

  const { accountLocked, userId } = useSelector(selectUserInfo)
  const isPeriodicReviewPage = location.pathname === `${PATHS.PERIODIC_REVIEW}`

  const {
    user,
    isTypeformSurveyVisible,
    isTypeformSurveyRequired,
    isLockedFeatureOn,
    isUserInfoInitialised,
    isAccountEnabled,
  } = useSelector((state: RootState) => state.route)

  useShield(userId)

  const fetchedNotifications = useSelector((state: RootState) => state.route.notifications)

  const { isTourRunning } = useSelector((state: RootState) => state.tour)

  const { isEarnEnabled } = useSelector((state: RootState) => state.earn)

  const { latestPersonalTransactions } = useSelector((state: RootState) => state.transactions)

  const { isAccountListInitiated } = useSelector((state: RootState) => state.accounts)

  const email = useSelector(selectUserEmail)

  const isKillSwitchActive = useSelector(selectIsKillSwitchActive)

  const isDpt2TncAccepted = useSelector(selectIsDpt2TncAccepted)

  const isOtcTrxHistoryEnabled = useSelector(selectIsOtcTrxHistoryEnabled)

  const flipperDpt2Tnc = useSelector(selectFlipperWithId(FLIPPER_KEYS.DPT2_TNC, email))

  // TODO(benedictkhoo): remove after assets unification is done
  const isAssetUnificationPhase1Ready = useSelector(selectIsAssetUnificationPhase1Ready)
  const isAssetUnificationPhase1Enabled = useSelector(selectIsAssetUnificationPhase1Enabled)

  // fetching application needed data
  useEffect(() => {
    dispatch(fetchBankList())
    dispatch(fetchUserInfo())
    dispatch(getTotalTravelRuleComplianceBlockerCount())
  }, [])

  useEffect(() => {
    if (!isAssetUnificationPhase1Ready) {
      return
    }

    if (isAssetUnificationPhase1Enabled) {
      dispatch(fetchAssetList())
    } else {
      // TODO(benedictkhoo): remove after assets unification is done
      dispatch(fetchAccountList())
      dispatch(fetchAccountDetailList())
    }
  }, [isAssetUnificationPhase1Ready, isAssetUnificationPhase1Enabled])

  useEffect(() => {
    if (!isAccountListInitiated || !isOtcTrxHistoryEnabled) {
      return
    }

    if (isAssetUnificationPhase1Enabled) {
      dispatch(fetchLatestTransactions())
    } else {
      // TODO(benedictkhoo): remove after assets unification is done
      dispatch(getLatestTransactions())
    }
  }, [isAccountListInitiated, isOtcTrxHistoryEnabled, isAssetUnificationPhase1Enabled])

  const sgdDgwAccount = useSelector(selectDgwAccountByCurrency)(CURRENCY.XSGD)
  useEffect(() => {
    if (!isAccountListInitiated) return

    if (sgdDgwAccount) {
      dispatch(fetchBlockchainAddresses(sgdDgwAccount.accountId))
    } else {
      dispatch(setBlockchainAddressList([]))
    }
  }, [dispatch, isAccountListInitiated, sgdDgwAccount?.accountId])

  useEffect(() => {
    if (latestPersonalTransactions && latestPersonalTransactions.length > 0) {
      const latestTransactionDate = new Date(latestPersonalTransactions[0].createdAt)
      const thresholdDate = new Date()
      thresholdDate.setDate(thresholdDate.getDate() - 90)

      dispatch(
        setIsTypeformSurveyRequired(latestTransactionDate.getTime() >= thresholdDate.getTime())
      )
    }
  }, [latestPersonalTransactions?.length])

  // TODO: move framework integration to hook
  useEffect(() => {
    if (isTypeformSurveyVisible && isTypeformSurveyRequired) {
      const script = document.createElement('script')

      script.src = 'https://embed.typeform.com/next/embed.js'
      script.async = true

      document.body.appendChild(script)
    }
  }, [isTypeformSurveyRequired, isTypeformSurveyVisible])

  const blockchainAddresses = useSelector(selectBlockchainAddressList)
  const isBlockchainAddressesInitiated = useSelector(selectIsBlockchainAddressesInitiated)
  const isUserFullyVerified = useSelector(selectIsUserFullyVerified)
  const verificationStatus = useSelector(selectUserVerificationStatus)
  const userName = useSelector(selectUserName)

  const [displayGetStartedTab, setDisplayGetStarted] = useState(false)

  useEffect(() => {
    if (verificationStatus && verificationStatus !== 'verified') {
      setDisplayGetStarted(true)
    }
  }, [isBlockchainAddressesInitiated, blockchainAddresses, verificationStatus])

  const requiresManualPeriodicReview = useSelector(selectUserRequiresManualPeriodicReview)

  const threeConsecutiveDigits = /\d{3}/
  const desktopBreakpointWidth = threeConsecutiveDigits.exec(DESKTOP_BREAKPOINT)?.[0] || 0
  const shouldRenderUserInfoBar =
    isLockedFeatureOn === false ||
    (isLockedFeatureOn === true && !(accountLocked && requiresManualPeriodicReview))

  // required for tour to work properly; prevents rendering antd Menu component twice
  const shouldRenderDesktopViewNavbar =
    (document.documentElement.clientWidth >= desktopBreakpointWidth || !isTourRunning) &&
    (isLockedFeatureOn === false ||
      (isLockedFeatureOn === true && !(accountLocked && requiresManualPeriodicReview))) &&
    !isPeriodicReviewPage

  const { ckaPassed } = useSelector(selectUserInfo)
  const { isCkaEnabled } = useSelector((state: RootState) => state.landingPage)
  const displayMintTab =
    isUserFullyVerified &&
    (!isCkaEnabled || ckaPassed || Moment().isBefore(CkaTransactionRestrictionDate))

  useEffect(() => {
    dispatch(init())
  }, []) // Run init just once

  const [filteredNotifications, setFilteredNotifications] = useState({
    data: [] as Notification[],
    unreadCount: 0,
  })

  const notifications = useMemo(
    () => ({
      isLoading: fetchedNotifications.isLoading,
      data: fetchedNotifications.data,
      unreadCount: fetchedNotifications.unreadCount,
    }),
    [fetchedNotifications.isLoading]
  )

  useEffect(() => {
    if (ConfigManager.isProd() && userId) {
      datadogRum.setUser({
        straitsx_user_id: userId,
      })
    }

    if (
      ConfigManager.getEnvironmentMode() === DEPLOY_ENVIRONMENT.PRODUCTION &&
      userId &&
      user?.kcId
    ) {
      posthog.identify(posthog.get_distinct_id(), { straitsx_user_id: userId, kcId: user?.kcId })
    }
  }, [user, userId])

  useEffect(() => {
    if (user?.email) {
      FeatureGatingManager.isEnabled(
        [
          'straitsx_typeform_survey',
          'straitsx_dpt_changeover',
          'xsgd_hts_withdrawal_fee_charging',
          'xsgd_dashboard_hedera_ui',
          'xsgd_dashboard_polygon_ui',
          'xsgd_dashboard_avalanche_ui',
          'usdt_dashboard_tron_ui',
          'xusd_dashboard_bnbsmart_app_ui',
          'xsgd_dashboard_arbitrum_ui',
          'xsgd_dashboard_ripple_ui',
          'whitelisting_improvement',
          'sso_two_fa_authenticator',
          'straitsx_dpt_risk_modal',
          'straitsx_enable_fazz_va_for_dashboard',
          'straitsx_enable_fazz_va_for_direct_mint',
          'straitsx_app_dashboard_use_earn',
          'straitsx_travel_rule_deposit_declaration',
          'straitsx_travel_rule_addr_whitelisting',
          'usdt_bep20_deposit',
          'usdt_bep20_withdrawal',
          'usdt_bep20_dashboard_ui',
          'straitsx_dashboard_xsgd_solana_ui',
          'straitsx_dashboard_xusd_solana_ui',
          'straitsx_dashboard_solana_ui',
        ],
        user?.email
      ).then(resp => {
        dispatch(setIsTypeformSurveyVisible(resp.straitsx_typeform_survey))
        dispatch(setIsDptApplied(resp.straitsx_dpt_changeover))
        dispatch(setIsHtsWithdrawalFeeCharging(resp.xsgd_hts_withdrawal_fee_charging))
        dispatch(setIsHederaUIEnabled(resp.xsgd_dashboard_hedera_ui))
        dispatch(setIsPolygonUIEnabled(resp.xsgd_dashboard_polygon_ui))
        dispatch(setIsAvalancheUIEnabled(resp.xsgd_dashboard_avalanche_ui))
        dispatch(setIsBnbsmartUIEnabled(resp.xusd_dashboard_bnbsmart_app_ui))
        dispatch(setIsTronUIEnabled(resp.usdt_dashboard_tron_ui))
        dispatch(setIsArbitrumUIEnabled(resp.xsgd_dashboard_arbitrum_ui))
        dispatch(setIsRippleUIEnabled(resp.xsgd_dashboard_ripple_ui))
        dispatch(setIsWhitelistingImprovementEnabled(resp.whitelisting_improvement))
        dispatch(setIsSsoTwoFaAuthenticatorEnabled(resp.sso_two_fa_authenticator))
        dispatch(setIsDptRiskModalEnabled(resp.straitsx_dpt_risk_modal))
        dispatch(setIsFazzVaForDashboardEnabled(resp.straitsx_enable_fazz_va_for_dashboard))
        dispatch(setIsFazzVaForDirectMintEnabled(resp.straitsx_enable_fazz_va_for_direct_mint))
        dispatch(setIsEarnEnabled(resp.straitsx_app_dashboard_use_earn))
        dispatch(
          setIsTravelRuleDepositDeclarationEnabled(resp.straitsx_travel_rule_deposit_declaration)
        )
        dispatch(
          setIsTravelRuleAddrWhitelistingEnabled(resp.straitsx_travel_rule_addr_whitelisting)
        )
        dispatch(setIsUsdtBep20DepositEnabled(resp.usdt_bep20_deposit))
        dispatch(setIsUsdtBep20WithdrawalEnabled(resp.usdt_bep20_withdrawal))
        dispatch(setIsUsdtBep20UIEnabled(resp.usdt_bep20_dashboard_ui))
        dispatch(setIsXsgdSplUIEnabled(resp.straitsx_dashboard_xsgd_solana_ui))
        dispatch(setIsXusdSplUIEnabled(resp.straitsx_dashboard_xusd_solana_ui))
        dispatch(setIsSolanaUIEnabled(resp.straitsx_dashboard_solana_ui))
      })
    }
  }, [user?.email])

  useAppFlippers({ email: user?.email })

  // New flippers should go here, check: https://www.notion.so/fazzfinancialgroup/StraitsX-Flipper-Usage-Guideline-08e54bedc7ed4d14b57d8b517ed00425
  useEffect(() => {
    if (user?.kcId) {
      FeatureGatingManager.isEnabled(
        ['straitsx_dashboard_cka'],
        `${ConfigManager.getEnvironmentMode()}_${ConfigManager.getSiteType()}_${user.kcId}`
      ).then(resp => {
        dispatch(setIsCkaEnabled(resp.straitsx_dashboard_cka))
      })
    }
  }, [user?.kcId])

  // double check the lock feature
  useEffect(() => {
    FeatureGatingManager.isEnabled('straitsx_locked_page').then(resp => {
      dispatch(setIsLockedFeatureOn(resp))
    })
  }, [])

  useEffect(() => {
    if (!isAccountEnabled) {
      cookie.remove('SESSIONID')
      SSOManager.ssoLogout()
    }
  }, [isAccountEnabled])

  const memoizedNav = useMemo(() => {
    return (
      <StraitsXNavigation
        displayGetStartedTab={displayGetStartedTab}
        displayMintTab={displayMintTab}
        isSandbox={isSandbox}
        displayEarnTab={isEarnEnabled}
      />
    )
  }, [isSandbox, displayGetStartedTab, displayMintTab, isEarnEnabled])

  if (isKillSwitchActive) {
    return <KillSwitchLockedPage />
  }

  if (
    !isDpt2TncAccepted &&
    isUserInfoInitialised &&
    !isKillSwitchActive &&
    flipperDpt2Tnc?.enabled
  ) {
    return <Dpt2Tnc siteType={SITE_TYPE.APP} />
  }

  return (
    <Root id="app-root">
      {isTypeformSurveyVisible && isTypeformSurveyRequired && (
        <div
          data-tf-popover="YsqtCzHk"
          data-tf-custom-icon="https://images.typeform.com/images/T62HziiCcpD9"
          data-tf-button-color="#00D37E"
          data-tf-button-text="Launch me"
          data-tf-iframe-props="title=StraitsX Relationship NPS Survey | XSGD"
          data-tf-medium="snippet"
          data-tf-hidden={{ utm_source: 'dashboard', id: user.email }}
          style={{ all: 'unset' }}
        ></div>
      )}
      <RouteHooks resetModal={resetModal} />
      {isKillSwitchActive !== undefined && (
        <Container>
          {shouldRenderDesktopViewNavbar && <NavigationWrapper>{memoizedNav}</NavigationWrapper>}
          <MainContainer>
            <ErrorBoundary key={location.pathname}>
              {shouldRenderUserInfoBar && (
                <StraitsXUserInfoBar
                  userInfo={{ ...user, fullName: userName }}
                  isSandbox={isSandbox}
                  notifications={notifications}
                  displayGetStartedTab={displayGetStartedTab}
                  displayMintTab={displayMintTab}
                  displayEarnTab={isEarnEnabled}
                />
              )}

              {// only show loading page when not receiving accountLocked from api yet
              accountLocked === null || isLockedFeatureOn === null ? (
                <LoadingPage />
              ) : (
                <Suspense fallback={<LoadingPage />}>
                  <Routes>
                    {straitsXRoutes.map((route, i) => (
                      <PrivateRoute
                        key={i}
                        {...route}
                        isAccountLocked={accountLocked}
                        isLockedFeatureOn={isLockedFeatureOn}
                        isPeriodicReviewRequired={requiresManualPeriodicReview}
                      />
                    ))}
                  </Routes>
                </Suspense>
              )}
              {!isPeriodicReviewPage && <StraitsXFooter />}
            </ErrorBoundary>
          </MainContainer>

          {isTourRunning && (
            <>
              <Global
                styles={css`
                  ${DESKTOP_BREAKPOINT} {
                    #react-joyride-step-3 > .__floater {
                      position: fixed !important;
                      transform: none !important;
                      top: 250px !important;
                      left: 240px !important;
                    }
                  }
                `}
              />
              <Tour />
            </>
          )}
        </Container>
      )}
    </Root>
  )
}

function LoadingPage() {
  const { spacing } = useTheme()

  return (
    <Page>
      <Card style={{ padding: spacing.xl }}>
        <Skeleton style={{ width: '40%' }} />
        <Skeleton />
        <Skeleton />
      </Card>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px;

  ${DESKTOP_BREAKPOINT} {
    padding: 24px;
  }
`

const Root = styled.div`
  z-index: 0;
`

const Container = styled.div`
  background-size: 100%;
  background-color: #f6f8f9;
  -webkit-font-smoothing: antialiased;
  display: flex;
`

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 73px;
  width: 100%;

  ${DESKTOP_BREAKPOINT} {
    padding-top: 0;
  }
`

const NavigationWrapper = styled.div`
  z-index: 1;
  display: none;

  ${DESKTOP_BREAKPOINT} {
    display: flex;
    width: 257px;
    flex-shrink: 0;
  }
`

const Skeleton = styled.div`
  float: left;
  width: 100%;
  height: 16px;

  margin: 10px;
  border-radius: 5px;
  background-size: 100wh;
  background-image: linear-gradient(90deg, #e8e8e8 0%, #f6f7f9 50%, #e8e8e8 100%);
  animation: shine-lines 1s infinite linear;

  @keyframes shine-lines {
    0% {
      background-position: -100px;
    }
    40%,
    100% {
      background-position: 140px;
    }
  }
`
